import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  MapPin,
  Clock,
  User,
  LogOut,
  Menu,
  X,
  Wifi,
  WifiOff
} from 'lucide-react';
import { Button } from '../ui/button';

const menuItems = [
  { path: '/delivery', icon: LayoutDashboard, label: 'Inicio' },
  { path: '/delivery/orders', icon: Package, label: 'Mis Pedidos' },
  { path: '/delivery/available', icon: MapPin, label: 'Disponibles' },
  { path: '/delivery/history', icon: Clock, label: 'Historial' },
  { path: '/delivery/profile', icon: User, label: 'Mi Perfil' },
];

const DeliveryLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  
  const deliveryUser = JSON.parse(localStorage.getItem('delivery_user') || '{}');
  const isOnline = deliveryUser?.delivery_profile?.is_online || false;
  const isVerified = deliveryUser?.delivery_profile?.is_verified || false;

  const handleLogout = () => {
    localStorage.removeItem('delivery_token');
    localStorage.removeItem('delivery_user');
    navigate('/delivery/login');
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Top bar - Mobile optimized */}
      <header className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-gray-400 hover:text-white md:hidden"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
          <span className="flex items-center gap-2">
            <img src="/logo-zonalibre.png" alt="Zona Libre" className="h-8" />
            <span className="text-xs text-gray-400">Delivery</span>
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Online status indicator */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
            isOnline ? 'bg-green-900/50 text-green-400' : 'bg-gray-700 text-gray-400'
          }`}>
            {isOnline ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            <span className="hidden sm:inline">{isOnline ? 'En línea' : 'Desconectado'}</span>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-gray-400 hover:text-red-400"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Verification warning */}
      {!isVerified && (
        <div className="bg-yellow-900/50 border-b border-yellow-700 px-4 py-3">
          <p className="text-yellow-400 text-sm text-center">
            ⚠️ Tu cuenta está pendiente de verificación. No podrás recibir pedidos hasta que un administrador apruebe tu cuenta.
          </p>
        </div>
      )}

      <div className="flex flex-1">
        {/* Sidebar - Hidden on mobile, toggle with menu button */}
        <aside className={`${
          menuOpen ? 'fixed inset-0 z-40 bg-gray-800' : 'hidden'
        } md:relative md:block w-64 bg-gray-800 border-r border-gray-700 md:w-20 lg:w-64`}>
          <nav className="py-4">
            <ul className="space-y-1 px-2">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-orange-500 text-white'
                          : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm font-medium md:hidden lg:inline">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User info at bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700">
            <div className="flex items-center gap-3">
              <img
                src={deliveryUser.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=driver'}
                alt="Avatar"
                className="w-10 h-10 rounded-full bg-gray-600"
              />
              <div className="flex-1 min-w-0 md:hidden lg:block">
                <p className="text-sm font-medium text-white truncate">{deliveryUser.name || 'Repartidor'}</p>
                <p className="text-xs text-gray-400">
                  {deliveryUser.delivery_profile?.vehicle_type === 'motorcycle' ? '🏍️ Moto' : 
                   deliveryUser.delivery_profile?.vehicle_type === 'bicycle' ? '🚲 Bicicleta' : '🚗 Auto'}
                </p>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          {children}
        </main>
      </div>

      {/* Bottom navigation for mobile */}
      <nav className="md:hidden bg-gray-800 border-t border-gray-700 px-2 py-2 sticky bottom-0">
        <ul className="flex justify-around">
          {menuItems.slice(0, 4).map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg ${
                    isActive ? 'text-orange-500' : 'text-gray-400'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default DeliveryLayout;
